/**
 * The loop, in order, with its settings and its total run time.
 *
 * The run time is the headline number and it sits at the top for a reason: a
 * store owner does not think in item durations, they think "the loop is four
 * minutes, and somebody waiting for a pizza sees it three times". It is computed
 * the way the TV will play it — image durations plus each video's natural length
 * times its repeat count — so it is a prediction, not a decoration.
 *
 * Saving sends the whole item list in one request, because that is what the API
 * takes and what the table stores: items are embedded in the playlist item, so a
 * reorder is one atomic write and a TV fetching its manifest mid-edit sees the
 * old list or the new one, never a list halfway through being rebuilt.
 */

import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import type { PlaylistItem } from '@pbtv/shared';
import { ErrorBlock, InlineEmpty } from '../Feedback.js';
import { errorMessage } from '../../lib/api.js';
import type { MediaAsset } from '../../lib/media.js';
import { saveIssues } from '../../lib/playlists.js';
import type { PlaylistRecord } from '../../lib/playlists.js';
import { PlaylistItemRow } from './PlaylistItemRow.js';
import {
  formatRuntime,
  moveItem,
  patchItemAt,
  playlistRuntime,
  removeAt,
  toPlaylistItems,
} from './runtime.js';
import type { DraftItem } from './runtime.js';

export interface PlaylistEditorProps {
  playlist: PlaylistRecord;
  /** Everything in this playlist's scope: its store's library plus shared media. */
  assets: readonly MediaAsset[];
  assetsError: unknown;
  items: readonly DraftItem[];
  onItemsChange: (items: readonly DraftItem[]) => void;
  dirty: boolean;
  saving: boolean;
  saveError: unknown;
  readOnly: boolean;
  onSave: () => void;
  onRevert: () => void;
  onAddMedia: () => void;
}

export function PlaylistEditor({
  playlist,
  assets,
  assetsError,
  items,
  onItemsChange,
  dirty,
  saving,
  saveError,
  readOnly,
  onSave,
  onRevert,
  onAddMedia,
}: PlaylistEditorProps): ReactElement {
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const [announcement, setAnnouncement] = useState('');

  const assetsById = useMemo(
    () => new Map(assets.map((asset) => [asset.assetId, asset])),
    [assets],
  );

  const plain: readonly PlaylistItem[] = useMemo(() => toPlaylistItems(items), [items]);
  const runtime = useMemo(
    () => playlistRuntime(plain, (assetId) => assetsById.get(assetId)),
    [plain, assetsById],
  );

  const titleOf = (row: DraftItem): string =>
    assetsById.get(row.item.assetId)?.title || row.item.assetId;

  const move = (from: number, to: number): void => {
    const row = items[from];
    if (row === undefined) return;

    const next = moveItem(items, from, to);
    if (next === items) {
      setAnnouncement(
        to < 0
          ? `${titleOf(row)} is already first.`
          : `${titleOf(row)} is already last.`,
      );
      return;
    }

    const landed = next.indexOf(row);
    setAnnouncement(`${titleOf(row)} moved to position ${landed + 1} of ${next.length}.`);
    onItemsChange(next);
  };

  const remove = (index: number): void => {
    const row = items[index];
    if (row === undefined) return;
    setAnnouncement(`${titleOf(row)} removed. ${items.length - 1} left.`);
    onItemsChange(removeAt(items, index));
  };

  const patch = (index: number, changes: Partial<PlaylistItem>): void => {
    onItemsChange(patchItemAt(items, index, changes));
  };

  const finishDrag = (): void => {
    setDragFrom(null);
    setDragOver(null);
  };

  const drop = (to: number): void => {
    // Dropping a row back where it started is not a failed move, so it is not
    // announced as one — it is somebody changing their mind.
    if (dragFrom !== null && dragFrom !== to) move(dragFrom, to);
    finishDrag();
  };

  return (
    <section className="panel">
      <div className="panel__head">
        <div>
          <h3 className="panel__title">{playlist.name}</h3>
          <p className="panel__subtitle">
            {summarise(items.length, runtime.totalMs, runtime.unknownCount)}
          </p>
          {runtime.missingCount > 0 ? (
            <p className="panel__note">
              {runtime.missingCount === 1 ? 'One item points' : `${runtime.missingCount} items point`}{' '}
              at media this playlist can no longer see — deleted, or moved out of its scope. The
              manifest builder drops them, so the TVs already skip them; removing them here stops
              them cluttering the list.
            </p>
          ) : null}
        </div>

        <div className="panel__actions">
          <button
            type="button"
            className="button button--ghost"
            onClick={onAddMedia}
            disabled={readOnly}
          >
            Add media
          </button>
          <button
            type="button"
            className="button button--ghost"
            onClick={onRevert}
            disabled={!dirty || saving}
          >
            Discard changes
          </button>
          <button
            type="button"
            className="button button--primary"
            onClick={onSave}
            disabled={!dirty || saving || readOnly}
          >
            {saving ? 'Saving…' : 'Save playlist'}
          </button>
        </div>
      </div>

      {readOnly ? (
        <p className="alert alert--info" role="note">
          This playlist is in the shared library, which only an administrator can change. You can
          still put it on your own screens.
        </p>
      ) : null}

      {dirty && !readOnly ? (
        <p className="alert alert--warning" role="status">
          Unsaved changes. Nothing reaches a TV until you save — the whole list is replaced in one
          request.
        </p>
      ) : null}

      {saveError !== null && saveError !== undefined ? (
        <div className="alert alert--danger" role="alert">
          <p>{errorMessage(saveError)}</p>
          {/* The API answers a refused save with one sentence per bad item, and
              which item is the only part worth reading in a twenty-row list. */}
          {saveIssues(saveError).length > 0 ? (
            <ul className="issue-list">
              {saveIssues(saveError).map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {assetsError !== null && assetsError !== undefined ? (
        <ErrorBlock error={assetsError} action="load the media for this playlist" />
      ) : null}

      {items.length === 0 ? (
        <InlineEmpty
          title="Nothing in this playlist yet"
          description="Add media to build the loop. Stills stay up for the number of seconds you set; videos play their own length, as many times through as you ask for."
        >
          <button
            type="button"
            className="button button--primary"
            onClick={onAddMedia}
            disabled={readOnly}
          >
            Add media
          </button>
        </InlineEmpty>
      ) : (
        <div className="table-scroll">
          <table className="data-table playlist-table">
            <thead>
              <tr>
                <th scope="col">Item</th>
                <th scope="col">Timing</th>
                <th scope="col">How it fills the screen</th>
                <th scope="col">Letterbox colour</th>
                <th scope="col">Length</th>
                <th scope="col">
                  <span className="visually-hidden">Order and removal</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((row, index) => (
                <PlaylistItemRow
                  key={row.uid}
                  row={row}
                  index={index}
                  total={items.length}
                  asset={assetsById.get(row.item.assetId)}
                  readOnly={readOnly}
                  isDragging={dragFrom === index}
                  isDropTarget={dragOver === index && dragFrom !== index}
                  onPatch={(changes) => patch(index, changes)}
                  onMove={(to) => move(index, to)}
                  onRemove={() => remove(index)}
                  onDragStart={() => setDragFrom(index)}
                  onDragEnterRow={() => setDragOver(index)}
                  onDragEnd={finishDrag}
                  onDrop={() => drop(index)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="visually-hidden" role="status" aria-live="polite">
        {announcement}
      </p>

      <p className="panel__note">
        Drag a row to reorder it, or use Up and Down — both do the same thing, and every change is
        announced for anyone not watching the list.
      </p>
    </section>
  );
}

function summarise(count: number, totalMs: number, unknownCount: number): string {
  if (count === 0) return 'Empty. A screen with only this playlist assigned shows the idle card.';

  const items = count === 1 ? '1 item' : `${count} items`;
  const length = formatRuntime(totalMs);

  if (unknownCount > 0) {
    const videos = unknownCount === 1 ? 'one video' : `${unknownCount} videos`;
    return `${items} · ${length} plus ${videos} of unrecorded length`;
  }

  return `${items} · ${length} for one time round the loop`;
}
